import type { Command } from '../../types/commands'
import { formatCommandOutput, colorize, formatHighlight, formatSuccess } from '../colorScheme'

export const projectsCommand: Command = {
  name: 'projects',
  description: 'View portfolio projects',
  aliases: ['portfolio', 'work'],
  category: 'info',
  execute: () => {
    const projectsText = `
┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓
┃                    ${formatHighlight('FEATURED PROJECTS')}                     ┃
┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
🎙️ ${formatHighlight('YAPS')} - Voice-to-Content Creation Tool
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Voice-first content creation. Record, transcribe with Whisper,
and generate polished blog posts — all from your voice.

<div style="display:flex;gap:0.75rem;flex-wrap:wrap;margin:0.75rem 0;">
  <img src="/screenshots/yaps.webp" alt="YAPS voice recorder with waveform visualizer" style="max-width:90%;width:320px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" />
  <img src="/screenshots/yaps2.webp" alt="YAPS generated blog post output" style="max-width:90%;width:320px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" />
</div>

${colorize('[████████████████]', 'brightGreen')} HTML/CSS/JS | Whisper | Claude AI | Express
Status: ${colorize('⚡ Active Development', 'yellow')}
Focus: Voice Interfaces | Content Creation

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
📝 ${formatHighlight('jott')} - AI-Powered Interactive Task Management CLI
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Keyboard-driven task management with AI integration, Google
Calendar sync, cross-project routing, and keyword automation.
Published on PyPI as jott-cli.

<div style="margin:0.75rem 0;"><img src="/screenshots/jott.webp" alt="jott CLI task management interface with ASCII logo and task list" style="max-width:98%;width:640px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /></div>

${colorize('[████████████████]', 'brightGreen')} Python | Claude AI | Google Calendar | MCP
Status: ${formatSuccess('✓ Published (PyPI)')}
Focus: Developer Tools | AI Integration

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
🔧 ${formatHighlight('jobsearch-cli')} - Intelligent Job Search Automation
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Comprehensive CLI for job search automation:
  • Multi-platform job board aggregation
  • AI-powered job matching (22-skill weighted system)
  • Automated cover letter generation
  • Browser extension for one-click applications

<div style="margin:0.75rem 0;"><img src="/screenshots/jobsearch-cli.webp" alt="jobsearch-cli intelligent job search automation interface" style="max-width:98%;width:640px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /></div>

${colorize('[████████████████]', 'brightGreen')} Go | SQLite | Claude AI | Cobra | Browser Extension
Status: ${formatSuccess('✓ Production')}
Focus: Systems Programming | AI Integration

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
🔊 ${formatHighlight('tts-extension')} - Text-to-Speech Browser Extension
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Chrome extension with multi-backend TTS: system voices,
OpenAI TTS, and Chatterbox AI voice cloning. Voice profile
management and accessibility-first design.

<div style="margin:0.75rem 0;"><img src="/screenshots/tts-extension.webp" alt="TTS Extension browser interface with voice profile management" style="max-width:98%;width:640px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /></div>

${colorize('[████████████████]', 'brightGreen')} JavaScript | Chrome Extension MV3 | OpenAI TTS | Chatterbox AI
Status: ${formatSuccess('✓ v2.0')}
Focus: Accessibility | AI Voice Cloning

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
🎤 ${formatHighlight('interviewer')} - Real-Time AI Interview Assistant
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Live audio capture with real-time Whisper transcription and
AI coaching via Claude. Dual mode support:
  • Interviewer: contextual question suggestions
  • Interviewee: STAR method coaching and response help

<div style="margin:0.75rem 0;"><img src="/screenshots/interviewer.webp" alt="Interviewer real-time interview assistant terminal interface" style="max-width:98%;width:640px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /></div>

${colorize('[████████████████]', 'brightGreen')} Go | PortAudio | Whisper | Claude AI | Bubble Tea TUI
Status: ${formatSuccess('✓ v1.0')}
Focus: Voice AI | Real-Time Processing

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
🧠 ${formatHighlight('therapissed')} - AI-Powered Political Satire Analysis
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Fetches real political quotes and delivers AI-powered
psychological analysis through a clinical psychologist persona.
Educational satire meets personality disorder awareness.

<div style="display:flex;gap:0.75rem;flex-wrap:wrap;margin:0.75rem 0;"><img src="/screenshots/therapissed1.webp" alt="Therapissed satire analysis landing page" style="max-width:31%;width:200px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /><img src="/screenshots/therapissed2.webp" alt="Therapissed quote display" style="max-width:31%;width:200px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /><img src="/screenshots/therapissed3.webp" alt="Therapissed clinical psychologist persona analysis" style="max-width:31%;width:200px;border-radius:6px;border:1px solid rgba(165,201,240,0.2);opacity:0.9;" /></div>

${colorize('[████████████████]', 'brightGreen')} Next.js | TypeScript | TailwindCSS | Claude AI | Neon Postgres
Status: ${formatSuccess('✓ Production')}
Focus: AI Personas | Educational Satire

╭──────────────────────────────────────────────────────────╮
│  All projects built with:                                │
│    ${formatSuccess('✓')} Human-centered design principles                   │
│    ${formatSuccess('✓')} Accessibility as core architecture                 │
│    ${formatSuccess('✓')} Transparent documentation                          │
│    ${formatSuccess('✓')} Open-source development                            │
╰──────────────────────────────────────────────────────────╯
`

    // Apply automatic formatting for technology names
    const formattedText = formatCommandOutput(projectsText, {
      highlightTech: true,
      highlightNumbers: true,
      highlightDates: false,
      highlightPaths: false,
      highlightUrls: true,
    })

    return [
      {
        type: 'info',
        content: (
          <div dangerouslySetInnerHTML={{ __html: formattedText }} />
        ),
      },
    ]
  },
}
